import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, ArrowUpCircle, ArrowLeftRight, Send, CreditCard, Users, History, 
  Shield, Settings, QrCode, Link2, Code, Grid3X3, Award, HelpCircle, 
  GraduationCap, LogOut, ChevronLeft, Check, Phone, Mail, Bell, Copy,
  Search, Download, ChevronDown, Calendar, CheckCircle
} from "lucide-react";

// Crypto icons as simple components
const CryptoIcon = ({ symbol }) => {
  const icons = {
    USDT: { bg: '#26A17B', text: 'T' },
    BTC: { bg: '#F7931A', text: '₿' },
    LTC: { bg: '#345D9D', text: 'Ł' },
    BNB: { bg: '#F3BA2F', text: 'B' },
    ETH: { bg: '#627EEA', text: 'Ξ' },
    TRX: { bg: '#FF0013', text: 'T' },
    USDC: { bg: '#2775CA', text: '$' },
  };
  const icon = icons[symbol] || { bg: '#666', text: '?' };
  return (
    <div 
      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
      style={{ backgroundColor: icon.bg }}
    >
      {icon.text}
    </div>
  );
};

// Generate random values outside component
const generateRandomId = () => Math.floor(100000 + Math.random() * 900000);
const generateBrowserTheme = () => Math.random() > 0.5 ? 'dark' : 'light';

export default function CashinoutCombinedPreview() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState('main'); // main, settings, payments
  const [browserTheme, setBrowserTheme] = useState(generateBrowserTheme);
  const [randomUserId] = useState(generateRandomId);
  const [balances, setBalances] = useState({});
  
  // Payment filters state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  // Set browser title and favicon
  useEffect(() => {
    const titles = {
      main: 'Главная',
      settings: 'Настройки пользователя',
      payments: 'История Платежей'
    };
    document.title = titles[currentPage] || 'Главная';
    
    // Check for theme in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');
    if (themeParam === 'dark' || themeParam === 'light') {
      setBrowserTheme(themeParam);
    }
    
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = '/cashinout-favicon.ico';
  }, [currentPage]);

  useEffect(() => {
    const dataParam = searchParams.get('data');
    if (dataParam) {
      try {
        const parsed = JSON.parse(decodeURIComponent(dataParam));
        setData(parsed);
        // Generate random balances up to 500
        setBalances({
          USDT: (Math.random() * 500).toFixed(2),
          BTC: (Math.random() * 0.01).toFixed(6),
          LTC: (Math.random() * 2).toFixed(4),
          BNB: (Math.random() * 1).toFixed(4),
          ETH: (Math.random() * 0.2).toFixed(5),
          TRX: (Math.random() * 500).toFixed(2),
          USDC: (Math.random() * 500).toFixed(2),
        });
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
  }, [searchParams]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center">
        <div className="text-gray-600">Загрузка...</div>
      </div>
    );
  }

  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.cashinoutId || randomUserId;
  const phoneNumber = data.phone || "+7 (999) 123-45-67";
  const email = data.email || "user@example.com";
  const allTransactions = data.transactions || [];

  // Theme colors
  const isDark = browserTheme === 'dark';
  const theme = {
    bg: isDark ? '#202124' : '#dee1e6',
    tabBarBg: isDark ? '#202124' : '#dee1e6',
    activeTabBg: isDark ? '#292a2d' : '#ffffff',
    inactiveTabBg: isDark ? '#35363a' : '#c9cdd4',
    addressBarBg: isDark ? '#292a2d' : '#ffffff',
    addressInputBg: isDark ? '#202124' : '#f1f3f4',
    textPrimary: isDark ? '#e8eaed' : '#202124',
    textSecondary: isDark ? '#9aa0a6' : '#5f6368',
    border: isDark ? '#3c4043' : '#dadce0',
  };

  const pageTitle = currentPage === 'main' ? 'Главная' : currentPage === 'settings' ? 'Настройки пользователя' : 'История Платежей';

  const menuItems = [
    { icon: Home, label: 'Главная', active: currentPage === 'main', page: 'main' },
    { icon: ArrowUpCircle, label: 'Пополнить', active: false },
    { icon: ArrowLeftRight, label: 'Обмен', active: false },
    { icon: Send, label: 'Отправить', active: false },
    { icon: CreditCard, label: 'Виртуальные карты', active: false },
    { icon: Users, label: 'Реферальная программа', active: false },
    { icon: History, label: 'История', active: currentPage === 'payments', page: 'payments' },
    { icon: Shield, label: '2FA', active: false },
    { icon: Settings, label: 'Настройки', active: currentPage === 'settings', page: 'settings' },
  ];

  const appItems = [
    { icon: QrCode, label: 'QR для авторизации' },
  ];

  const businessItems = [
    { icon: Link2, label: 'Платёжные ссылки' },
    { icon: Code, label: 'API' },
    { icon: Grid3X3, label: 'Массовые выплаты' },
  ];

  const bottomItems = [
    { icon: Award, label: 'Тарифы' },
    { icon: HelpCircle, label: 'Служба поддержки' },
    { icon: GraduationCap, label: 'Академия Cashinout' },
    { icon: HelpCircle, label: 'Частые вопросы' },
    { icon: LogOut, label: 'Выйти' },
  ];

  const cryptoList = [
    { symbol: 'USDT', name: 'Tether' },
    { symbol: 'BTC', name: 'Bitcoin' },
    { symbol: 'LTC', name: 'Litecoin' },
    { symbol: 'BNB', name: 'Binance Coin' },
    { symbol: 'ETH', name: 'Ethereum' },
    { symbol: 'TRX', name: 'TRON' },
    { symbol: 'USDC', name: 'USD Coin' },
  ];

  // Browser frame component - exact copy from CashinoutMainPreview
  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
        <div className="flex items-end flex-1 pl-2">
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] group cursor-pointer" style={{ backgroundColor: theme.inactiveTabBg }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
          </div>
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] group cursor-pointer" style={{ backgroundColor: theme.activeTabBg }}>
            <img src="/cashinout-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>{pageTitle}</span>
          </div>
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isDark ? '#5f6368' : '#dadce0' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: theme.addressInputBg, border: `1px solid ${theme.border}` }}>
          <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: theme.textSecondary }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: theme.textPrimary }}>cashinout.io/{currentPage === 'main' ? 'dashboard' : currentPage === 'settings' ? 'profile' : 'history'}</span>
        </div>
        <div className="flex items-center gap-1 ml-3">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
        </div>
      </div>
    </div>
  );

  // ============================================
  // MAIN PAGE CONTENT - exact copy from CashinoutMainPreview
  // ============================================
  const MainPageContent = () => (
    <>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-end gap-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="text-lg">🇷🇺</span>
          <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
        </div>
        <span className="text-sm text-gray-700 font-medium">{accountName}</span>
        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
          <span className="text-white text-xs font-medium">
            {accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </span>
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <span>ID:</span>
          <span className="font-medium">{userId}</span>
        </div>
        <button className="p-2 text-gray-500 hover:text-gray-700">
          <Copy className="w-5 h-5" />
        </button>
        <button className="p-2 text-gray-500 hover:text-gray-700 relative">
          <Bell className="w-5 h-5" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
        </button>
      </header>

      {/* Page Content */}
      <main className="flex-1 p-6 overflow-auto">
        <div className="flex gap-6">
          {/* Left Column */}
          <div className="flex-1 min-w-0">
            {/* Page Title */}
            <div className="flex items-center gap-3 mb-6">
              <h1 className="text-2xl font-semibold text-gray-900">Главная</h1>
              <div className="flex items-center gap-1 text-green-500 text-sm">
                <span>Аккаунт верифицирован</span>
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            </div>

            {/* Wallet Card */}
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a87] rounded-xl p-5 mb-6">
              <h2 className="text-white font-medium text-lg mb-2">Мой Кошелек</h2>
              <p className="text-gray-300 text-sm mb-1">Основной баланс в USDT</p>
              <div className="flex items-center gap-2">
                <span className="text-white text-3xl font-bold">{balances.USDT || '0'}</span>
                <span className="text-white text-xl">USDT</span>
                <button className="ml-2 text-gray-300 hover:text-white">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2 mt-4">
                <button className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors">
                  <ArrowLeftRight className="w-4 h-4" />
                  <span>Обмен</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors">
                  <Send className="w-4 h-4" />
                  <span>Отправить</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors">
                  <ArrowUpCircle className="w-4 h-4" />
                  <span>Получить</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-[#3b82f6] hover:bg-[#2563eb] rounded-lg text-white text-sm transition-colors ml-auto">
                  <CreditCard className="w-4 h-4" />
                  <span>Оплатить</span>
                </button>
              </div>
            </div>

            {/* My Accounts */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h3 className="font-medium text-gray-900">Мои аккаунты</h3>
                <button className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1">
                  Скрыть нулевые балансы
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
              <div className="divide-y divide-gray-100">
                {cryptoList.map((crypto) => (
                  <div key={crypto.symbol} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <CryptoIcon symbol={crypto.symbol} />
                      <div>
                        <div className="font-medium text-gray-900">{crypto.symbol}</div>
                        <div className="text-xs text-gray-500">{crypto.name}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-medium text-gray-900">{balances[crypto.symbol] || '0'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - News */}
          <div className="w-96 flex-shrink-0">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Новости</h3>
            
            {/* News Cards */}
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-600 to-purple-500 rounded-xl p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-sm mb-1">ОПЛАЧИВАЙ STEAM ПРЯМО ИЗ</h4>
                    <p className="text-sm opacity-90">ЛИЧНОГО КАБИНЕТА CASHINOUT</p>
                  </div>
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🎮</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-sm mb-1">ЧИТАЙ СВЕЖИЕ НОВОСТИ</h4>
                    <p className="text-sm opacity-90">В НАШЕМ ТГ-КАНАЛЕ</p>
                  </div>
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">📱</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-sm mb-1">СКАЧИВАЙ ПРИЛОЖЕНИЕ</h4>
                    <p className="text-sm opacity-90">В GOOGLE PLAY</p>
                  </div>
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                    alt="Google Play"
                    className="h-10"
                  />
                </div>
              </div>
            </div>

            {/* Info Cards */}
            <div className="mt-6 space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">🛒</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">Платите в магазинах по QR коду сразу из крипты</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Теперь можно не выводить криптовалюту в фиат, а платить в оффлайн магазинах сразу из нашего кошелька. 
                      Фактически вы платите в рублях на кассе, а мы очень быстро обеспечиваем конвертацию из USDT в рубли.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">💳</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">Виртуальные карты для оплат сервисов по всему миру</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Теперь у нас карты с 3d secure работают стабильнее и позволяют оплачивать тысячи сервисов: 
                      ChatGPT, Midjourney, Netflix, Canva, GoogleAds, Facebook, Perplexity, RenderAI и тп. 
                      Открываются за пару секунд.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Нашими картами нельзя платить в рублях и валютах санкционных стран.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );

  // ============================================
  // SETTINGS PAGE CONTENT - exact copy from CashinoutPreview
  // ============================================
  const SettingsPageContent = () => (
    <>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-end gap-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="text-lg">🇷🇺</span>
          <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
        </div>
        <span className="text-sm text-gray-700 font-medium">{accountName}</span>
        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
          <span className="text-white text-xs font-medium">
            {accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </span>
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <span>ID:</span>
          <span className="font-medium">{userId}</span>
        </div>
        <button className="p-2 text-gray-500 hover:text-gray-700">
          <Copy className="w-5 h-5" />
        </button>
        <button className="p-2 text-gray-500 hover:text-gray-700 relative">
          <Bell className="w-5 h-5" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
        </button>
      </header>

      {/* Page Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold text-[#1a1a2e] mb-8">Настройки</h1>

          {/* Account Name */}
          <div className="mb-6">
            <label className="block text-sm text-[#64748b] mb-2">Имя аккаунта:</label>
            <div className="relative">
              <input
                type="text"
                value={accountName}
                readOnly
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-[#1a1a2e] bg-white focus:outline-none focus:border-[#00c853]"
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-[#00c853] rounded flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Phone Number */}
          <div className="mb-6">
            <label className="block text-sm text-[#64748b] mb-2">Номер телефона:</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <Phone className="w-5 h-5 text-[#64748b]" />
              </div>
              <input
                type="text"
                value={phoneNumber}
                readOnly
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-[#1a1a2e] bg-white focus:outline-none focus:border-[#00c853]"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 text-[#00c853] text-sm">
                <Check className="w-4 h-4" />
                <span>Подтверждён</span>
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="mb-6">
            <label className="block text-sm text-[#64748b] mb-2">Email:</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <Mail className="w-5 h-5 text-[#64748b]" />
              </div>
              <input
                type="text"
                value={email}
                readOnly
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-[#1a1a2e] bg-white focus:outline-none focus:border-[#00c853]"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 text-[#00c853] text-sm">
                <Check className="w-4 h-4" />
                <span>Подтверждён</span>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-[#e3f2fd] border border-[#90caf9] rounded-lg p-4 mb-8">
            <p className="text-sm text-[#1565c0]">
              Ниже вы можете привязать или изменить свой аккаунт Google или Telegram для удобного и быстрого входа в кошелёк
            </p>
          </div>

          {/* Important Warning */}
          <div className="border border-gray-200 rounded-lg p-4 mb-8">
            <p className="text-sm font-semibold text-[#1a1a2e] mb-2">Важно:</p>
            <p className="text-sm text-[#64748b]">
              После привязки аккаунта ваш кошелёк будет временно заморожен на 24 часа в целях безопасности. В этот период вывод средств и некоторые операции будут недоступны.
            </p>
          </div>

          {/* Link Google Account */}
          <div className="mb-6">
            <label className="block text-sm text-[#64748b] mb-3 text-center">Привязать аккаунт Google:</label>
            <button className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-[#1a1a2e] font-medium">Google</span>
            </button>
          </div>

          {/* Link Telegram Account */}
          <div className="mb-8">
            <label className="block text-sm text-[#64748b] mb-3 text-center">Привязать аккаунт Telegram:</label>
            <button className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#0088cc] text-white rounded-lg hover:bg-[#0077b5] transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              <span className="font-medium">Войти через Telegram</span>
            </button>
          </div>

          {/* Additional Info */}
          <div className="text-sm text-[#64748b] space-y-4">
            <p>После привязки аккаунта вы сможете использовать его для быстрого входа без ввода пароля</p>
            
            <div>
              <p className="font-semibold text-[#1a1a2e]">Изменение (перепривязка) аккаунта</p>
              <p>Если вы хотите изменить ранее привязанный аккаунт Telegram или Google, выберите новый способ авторизации и следуйте инструкциям. Помните, что после перепривязки ваш кошелёк будет временно заморожен на 24 часа в целях безопасности.</p>
            </div>

            <p>Если вы хотите полностью удалить свой аккаунт, нажмите кнопку ниже:</p>
            
            <button className="w-full px-4 py-3 border border-red-300 text-red-500 rounded-lg hover:bg-red-50 transition-colors">
              Удалить аккаунт
            </button>
          </div>
        </div>
      </main>
    </>
  );

  // ============================================
  // PAYMENTS PAGE CONTENT - with working filters
  // ============================================
  const PaymentsPageContent = () => {
    // Get type label
    const getTypeLabel = (tx) => {
      if (tx.type === 'deposit') return 'Пополнение';
      if (tx.type === 'withdrawal') return 'Вывод';
      return 'Перевод';
    };

    // Get type filter label for dropdown button
    const getTypeFilterLabel = () => {
      if (typeFilter === 'deposit') return 'Пополнение';
      if (typeFilter === 'withdrawal') return 'Вывод';
      return 'Все';
    };

    // Get date range label
    const getDateRangeLabel = () => {
      if (!dateFrom && !dateTo) return 'Все время';
      if (dateFrom && dateTo) return `${dateFrom} - ${dateTo}`;
      if (dateFrom) return `с ${dateFrom}`;
      if (dateTo) return `до ${dateTo}`;
      return 'Все время';
    };

    // Clear date filters
    const clearDateFilter = () => {
      setDateFrom('');
      setDateTo('');
      setShowDatePicker(false);
    };

    // Parse date string "DD.MM.YYYY" to Date object
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const parts = dateStr.split('.');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]);
      }
      return null;
    };

    // Filter transactions by date range and type
    const filteredTransactions = allTransactions.filter(tx => {
      // Type filter
      if (typeFilter !== 'all') {
        if (typeFilter === 'deposit' && tx.type !== 'deposit') return false;
        if (typeFilter === 'withdrawal' && tx.type !== 'withdrawal') return false;
      }
      
      // Date filter
      if (!dateFrom && !dateTo) return true;
      
      const txDate = parseDate(tx.date);
      if (!txDate) return true;
      
      if (dateFrom) {
        const fromDate = parseDate(dateFrom);
        if (fromDate) {
          fromDate.setHours(0, 0, 0, 0);
          if (txDate < fromDate) return false;
        }
      }
      
      if (dateTo) {
        const toDate = parseDate(dateTo);
        if (toDate) {
          toDate.setHours(23, 59, 59, 999);
          if (txDate > toDate) return false;
        }
      }
      
      return true;
    });

    // Calculate running balance
    let runningBalance = data.balance || 0;

    return (
      <>
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-end gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="text-lg">🇷🇺</span>
            <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
          </div>
          <span className="text-sm text-gray-700 font-medium">{accountName}</span>
          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-medium">
              {accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <span>ID:</span>
            <span className="font-medium">{userId}</span>
          </div>
          <button className="p-2 text-gray-500 hover:text-gray-700">
            <Copy className="w-5 h-5" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
          </button>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-5xl">
            <h1 className="text-2xl font-semibold text-gray-900 mb-6">История</h1>

            {/* Filters Row */}
            <div className="flex items-center gap-4 mb-6">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Поиск"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white"
                />
              </div>

              {/* All Dropdown */}
              <div className="relative">
                <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white">
                  <span className="text-gray-900">Все</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              {/* Export Button */}
              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white">
                <span className="text-gray-900">Экспорт</span>
                <Download className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Filter Dropdowns */}
            <div className="flex items-center gap-4 mb-6">
              {/* Currency */}
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Валюта</label>
                <button className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white">
                  <span className="text-gray-900">RUB</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              {/* Type Filter */}
              <div className="flex-1 relative">
                <label className="block text-xs text-gray-500 mb-1">Тип</label>
                <button 
                  onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white"
                >
                  <span className="text-gray-900">{getTypeFilterLabel()}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                
                {/* Type Dropdown */}
                {showTypeDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => { setTypeFilter('all'); setShowTypeDropdown(false); }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${typeFilter === 'all' ? 'bg-blue-50 text-blue-600' : 'text-gray-900'}`}
                    >
                      Все
                    </button>
                    <button
                      onClick={() => { setTypeFilter('deposit'); setShowTypeDropdown(false); }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${typeFilter === 'deposit' ? 'bg-blue-50 text-blue-600' : 'text-gray-900'}`}
                    >
                      Пополнение
                    </button>
                    <button
                      onClick={() => { setTypeFilter('withdrawal'); setShowTypeDropdown(false); }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${typeFilter === 'withdrawal' ? 'bg-blue-50 text-blue-600' : 'text-gray-900'}`}
                    >
                      Вывод
                    </button>
                  </div>
                )}
              </div>

              {/* Date Filter */}
              <div className="flex-1 relative">
                <label className="block text-xs text-gray-500 mb-1">Дата</label>
                <button 
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white"
                >
                  <span className="text-gray-900 truncate">{getDateRangeLabel()}</span>
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                </button>
                
                {/* Date Picker Dropdown */}
                {showDatePicker && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">С даты (ДД.ММ.ГГГГ)</label>
                        <input
                          type="text"
                          placeholder="01.01.2025"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">По дату (ДД.ММ.ГГГГ)</label>
                        <input
                          type="text"
                          placeholder="31.12.2025"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={clearDateFilter}
                          className="flex-1 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          Сбросить
                        </button>
                        <button
                          onClick={() => setShowDatePicker(false)}
                          className="flex-1 px-3 py-2 text-sm text-white bg-blue-500 rounded-lg hover:bg-blue-600"
                        >
                          Применить
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Статус</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Время</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Валюта</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Сумма</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Баланс</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Тип</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                        {(dateFrom || dateTo || typeFilter !== 'all') && allTransactions.length > 0 
                          ? 'Нет операций по выбранным фильтрам' 
                          : 'Список операций пуст'}
                      </td>
                    </tr>
                  ) : (
                    filteredTransactions.map((tx, index) => {
                      const isDeposit = tx.type === 'deposit';
                      const amount = tx.amount || 0;
                      
                      // Calculate balance for this row
                      if (index > 0) {
                        const prevTx = filteredTransactions[index - 1];
                        if (prevTx.type === 'deposit') {
                          runningBalance -= prevTx.amount || 0;
                        } else {
                          runningBalance += prevTx.amount || 0;
                        }
                      }
                      
                      return (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                                <Check className="w-3.5 h-3.5 text-green-500" />
                              </div>
                              <span className="text-sm text-green-500">Успешно</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">{tx.date}</div>
                            <div className="text-xs text-gray-400">{tx.time || '00:00'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-900">RUB</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm font-medium ${isDeposit ? 'text-green-500' : 'text-red-500'}`}>
                              {isDeposit ? '+' : '-'}{amount.toLocaleString('ru-RU')} ₽
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-900">{runningBalance.toLocaleString('ru-RU')} ₽</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-900">{getTypeLabel(tx)}</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </>
    );
  };

  return (
    <div className="min-h-screen flex flex-col template-preview-scale" style={{ backgroundColor: theme.bg, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <BrowserFrame />
      
      {/* Main Content (Website) */}
      <div className="flex-1 flex bg-[#f5f7fa]">
        {/* Sidebar - with navigation */}
        <aside className={`${menuCollapsed ? 'w-16' : 'w-56'} bg-[#303646] flex flex-col transition-all duration-300 flex-shrink-0`}>
          {/* Logo */}
          <div className="p-4 flex items-center gap-3">
            <img 
              src="/cashinout-logo.png" 
              alt="Cashinout" 
              className={`${menuCollapsed ? 'w-8' : 'h-8'} object-contain`}
            />
          </div>

          {/* Main Menu */}
          <nav className="flex-1 py-2 overflow-y-auto">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isClickable = item.page;
              return (
                <a
                  key={index}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (isClickable) {
                      setCurrentPage(item.page);
                    }
                  }}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                    item.active 
                      ? 'text-white bg-[#3d4557]' 
                      : 'text-[#9ca3af] hover:bg-[#3d4557] hover:text-white'
                  } ${isClickable ? 'cursor-pointer' : ''}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!menuCollapsed && <span>{item.label}</span>}
                </a>
              );
            })}

            {/* App Section */}
            {!menuCollapsed && (
              <div className="px-4 py-2 mt-4">
                <span className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Приложение</span>
              </div>
            )}
            {appItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <a
                  key={index}
                  href="#"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af] hover:bg-[#3d4557] hover:text-white transition-colors"
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!menuCollapsed && <span>{item.label}</span>}
                </a>
              );
            })}

            {/* For Business Section */}
            {!menuCollapsed && (
              <div className="px-4 py-2 mt-4">
                <span className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Для бизнеса</span>
              </div>
            )}
            {businessItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <a
                  key={index}
                  href="#"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af] hover:bg-[#3d4557] hover:text-white transition-colors"
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!menuCollapsed && <span>{item.label}</span>}
                </a>
              );
            })}

            {/* Bottom Items */}
            <div className="mt-4 border-t border-[#3d4557] pt-2">
              {bottomItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <a
                    key={index}
                    href="#"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af] hover:bg-[#3d4557] hover:text-white transition-colors"
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!menuCollapsed && <span>{item.label}</span>}
                  </a>
                );
              })}
            </div>
          </nav>

          {/* Hide Menu Toggle */}
          <button 
            onClick={() => setMenuCollapsed(!menuCollapsed)}
            className="flex items-center gap-3 px-4 py-3 text-sm text-[#9ca3af] hover:bg-[#3d4557] border-t border-[#3d4557]"
          >
            <div className={`w-5 h-3 rounded-full bg-[#4a5568] flex items-center px-0.5 transition-all ${menuCollapsed ? 'justify-end' : 'justify-start'}`}>
              <div className="w-2 h-2 rounded-full bg-white"></div>
            </div>
            {!menuCollapsed && <span>Скрыть меню</span>}
          </button>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {currentPage === 'main' && <MainPageContent />}
          {currentPage === 'settings' && <SettingsPageContent />}
          {currentPage === 'payments' && <PaymentsPageContent />}
        </div>
      </div>
    </div>
  );
}
